import { useRef, useState } from 'react';
import { Plus, X, Wand2, Upload, ImageIcon, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { adminApi } from '@/services/api';
import { Badge, Button, Input } from '@nepal-ecom/ui';

/**
 * Variant editor that:
 *   1. Lets the admin define one or more "attributes" (Color, Size, RAM…).
 *   2. Adds values per attribute as chips.
 *   3. On "Generate", creates the cartesian product as variant rows with
 *      editable SKU / price / stock per combination.
 */

export interface VariantRow {
  id: string;                       // local-only key (e.g. "color:Red|size:M")
  name: string;                     // "Red / M"
  options: Record<string, string>;  // { color: 'Red', size: 'M' }
  sku: string;
  price: number;
  stock: number;
  imageUrl?: string;                // per-variant image (relative /uploads path)
}

export interface VariantState {
  attributes: Array<{ key: string; label: string; values: string[] }>;
  variants: VariantRow[];
}

interface Preset {
  id: string;
  label: string;
  attributes: Array<{ key: string; label: string; defaultValues?: string[] }>;
}

// Sensible defaults — admin can still customize after applying.
export const VARIANT_PRESETS: Preset[] = [
  {
    id: 'clothing',
    label: 'Clothing',
    attributes: [
      { key: 'size',  label: 'Size',  defaultValues: ['XS', 'S', 'M', 'L', 'XL', 'XXL'] },
      { key: 'color', label: 'Color', defaultValues: ['Black', 'White'] },
    ],
  },
  {
    id: 'footwear',
    label: 'Footwear / Sneakers',
    attributes: [
      { key: 'size',  label: 'Size (UK)', defaultValues: ['6', '7', '8', '9', '10', '11'] },
      { key: 'color', label: 'Color',     defaultValues: ['Black', 'White'] },
    ],
  },
  {
    id: 'smartphone',
    label: 'Smartphones',
    attributes: [
      { key: 'storage', label: 'Storage', defaultValues: ['128GB', '256GB', '512GB'] },
      { key: 'ram',     label: 'RAM',     defaultValues: ['6GB', '8GB', '12GB'] },
      { key: 'color',   label: 'Color',   defaultValues: ['Black', 'Silver'] },
    ],
  },
  {
    id: 'laptop',
    label: 'Laptops',
    attributes: [
      { key: 'processor', label: 'Processor', defaultValues: ['i5', 'i7', 'Ryzen 5'] },
      { key: 'ram',       label: 'RAM',       defaultValues: ['8GB', '16GB', '32GB'] },
      { key: 'storage',   label: 'Storage',   defaultValues: ['256GB SSD', '512GB SSD', '1TB SSD'] },
    ],
  },
];

const slugifyKey = (s: string) =>
  s.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '') || 'attr';

function cartesian(arrays: string[][]): string[][] {
  if (arrays.length === 0) return [];
  return arrays.reduce<string[][]>(
    (acc, curr) => acc.flatMap((c) => curr.map((v) => [...c, v])),
    [[]],
  );
}

interface Props {
  basePrice: number;
  baseStock: number;
  value: VariantState;
  onChange: (next: VariantState) => void;
}

export function VariantEditor({ basePrice, baseStock, value, onChange }: Props) {
  const [newAttrLabel, setNewAttrLabel] = useState('');

  const addAttribute = (label: string, defaultValues: string[] = []) => {
    const cleanLabel = label.trim();
    if (!cleanLabel) return;
    const key = slugifyKey(cleanLabel);
    if (value.attributes.some((a) => a.key === key)) return;
    onChange({
      ...value,
      attributes: [...value.attributes, { key, label: cleanLabel, values: defaultValues }],
    });
    setNewAttrLabel('');
  };

  const removeAttribute = (key: string) => {
    onChange({
      attributes: value.attributes.filter((a) => a.key !== key),
      variants: [],
    });
  };

  const addValue = (attrKey: string, raw: string) => {
    const val = raw.trim();
    if (!val) return;
    onChange({
      ...value,
      attributes: value.attributes.map((a) =>
        a.key === attrKey && !a.values.includes(val)
          ? { ...a, values: [...a.values, val] }
          : a,
      ),
    });
  };

  const removeValue = (attrKey: string, val: string) => {
    onChange({
      ...value,
      attributes: value.attributes.map((a) =>
        a.key === attrKey ? { ...a, values: a.values.filter((v) => v !== val) } : a,
      ),
    });
  };

  const applyPreset = (preset: Preset) => {
    const attributes = preset.attributes.map((a) => ({
      key: a.key,
      label: a.label,
      values: a.defaultValues ?? [],
    }));
    onChange({ attributes, variants: [] });
  };

  const generateVariants = () => {
    const eligible = value.attributes.filter((a) => a.values.length > 0);
    if (eligible.length === 0) return;

    const valueGrid = eligible.map((a) => a.values);
    const combos = cartesian(valueGrid);

    const next: VariantRow[] = combos.map((combo) => {
      const options: Record<string, string> = {};
      eligible.forEach((a, i) => { options[a.key] = combo[i]; });
      const id = eligible.map((a) => `${a.key}:${options[a.key]}`).join('|');
      const name = combo.join(' / ');

      // Preserve existing edits when re-generating
      const existing = value.variants.find((v) => v.id === id);
      return existing ?? {
        id,
        name,
        options,
        sku: '',
        price: basePrice,
        stock: baseStock,
      };
    });

    onChange({ ...value, variants: next });
  };

  const patchVariant = (id: string, patch: Partial<VariantRow>) => {
    onChange({
      ...value,
      variants: value.variants.map((v) => (v.id === id ? { ...v, ...patch } : v)),
    });
  };

  const removeVariant = (id: string) => {
    onChange({ ...value, variants: value.variants.filter((v) => v.id !== id) });
  };

  return (
    <div className="space-y-5">
      {/* Presets */}
      {value.attributes.length === 0 && (
        <div className="rounded-lg bg-slate-50 ring-1 ring-slate-200 p-4">
          <p className="text-xs font-semibold text-slate-600 uppercase tracking-wider mb-2.5">
            Quick start — pick a preset
          </p>
          <div className="flex flex-wrap gap-2">
            {VARIANT_PRESETS.map((p) => (
              <Button
                key={p.id}
                type="button"
                variant="outline"
                size="sm"
                onClick={() => applyPreset(p)}
              >
                {p.label}
              </Button>
            ))}
          </div>
        </div>
      )}

      {/* Attributes */}
      <div className="space-y-3">
        {value.attributes.map((a) => (
          <AttributeRow
            key={a.key}
            attr={a}
            onAddValue={(v) => addValue(a.key, v)}
            onRemoveValue={(v) => removeValue(a.key, v)}
            onRemove={() => removeAttribute(a.key)}
          />
        ))}

        <div className="flex gap-2 items-center">
          <Input
            value={newAttrLabel}
            onChange={(e) => setNewAttrLabel(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') { e.preventDefault(); addAttribute(newAttrLabel); }
            }}
            placeholder="Add attribute (e.g. Color, Size, RAM, Storage)"
            className="max-w-sm"
          />
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => addAttribute(newAttrLabel)}
            disabled={!newAttrLabel.trim()}
          >
            <Plus className="h-3.5 w-3.5" /> Add
          </Button>
        </div>
      </div>

      {/* Generate */}
      {value.attributes.length > 0 && value.attributes.some((a) => a.values.length > 0) && (
        <div className="flex items-center justify-between p-3 rounded-lg border border-dashed border-slate-300 bg-amber-50/40">
          <p className="text-xs text-slate-600">
            Click "Generate" to create one variant row for every combination of values above.
            <span className="text-slate-400"> Existing edits are preserved.</span>
          </p>
          <Button type="button" size="sm" onClick={generateVariants}>
            <Wand2 className="h-3.5 w-3.5" /> Generate variants
          </Button>
        </div>
      )}

      {/* Variant table */}
      {value.variants.length > 0 && (
        <div className="overflow-x-auto rounded-lg ring-1 ring-slate-200">
          <table className="w-full">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200">
                <th className="px-3 py-2 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Variant</th>
                <th className="px-3 py-2 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Image</th>
                <th className="px-3 py-2 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider">SKU</th>
                <th className="px-3 py-2 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Price (NPR)</th>
                <th className="px-3 py-2 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Stock</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {value.variants.map((v) => (
                <tr key={v.id} className="hover:bg-slate-50">
                  <td className="px-3 py-2 text-sm font-medium text-slate-800 whitespace-nowrap">{v.name}</td>
                  <td className="px-3 py-2">
                    <VariantImageCell
                      value={v.imageUrl}
                      onChange={(url) => patchVariant(v.id, { imageUrl: url })}
                    />
                  </td>
                  <td className="px-3 py-2">
                    <Input
                      value={v.sku}
                      onChange={(e) => patchVariant(v.id, { sku: e.target.value })}
                      placeholder="Auto"
                      className="h-8 text-xs font-mono min-w-[140px]"
                    />
                  </td>
                  <td className="px-3 py-2">
                    <Input
                      type="number" min="0" step="0.01"
                      value={v.price}
                      onChange={(e) => patchVariant(v.id, { price: Number(e.target.value) })}
                      className="h-8 text-sm tabular-nums w-28"
                    />
                  </td>
                  <td className="px-3 py-2">
                    <Input
                      type="number" min="0" step="1"
                      value={v.stock}
                      onChange={(e) => patchVariant(v.id, { stock: Math.max(0, Math.floor(Number(e.target.value))) })}
                      className="h-8 text-sm tabular-nums w-20"
                    />
                  </td>
                  <td className="px-3 py-2 text-right">
                    <button
                      type="button"
                      onClick={() => removeVariant(v.id)}
                      className="text-slate-400 hover:text-red-500 p-1"
                      title="Remove variant"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─── Internal: per-attribute row with chip-style values ──────────────────────

interface AttrRowProps {
  attr: { key: string; label: string; values: string[] };
  onAddValue: (v: string) => void;
  onRemoveValue: (v: string) => void;
  onRemove: () => void;
}

function AttributeRow({ attr, onAddValue, onRemoveValue, onRemove }: AttrRowProps) {
  const [draft, setDraft] = useState('');

  return (
    <div className="rounded-lg border border-slate-200 p-3 bg-white">
      <div className="flex items-center justify-between mb-2">
        <p className="text-sm font-bold text-slate-900">{attr.label}</p>
        <button
          type="button"
          onClick={onRemove}
          className="text-xs text-slate-400 hover:text-red-500 inline-flex items-center gap-1"
        >
          <X className="h-3 w-3" /> Remove attribute
        </button>
      </div>

      <div className="flex flex-wrap items-center gap-1.5 mb-2.5">
        {attr.values.map((v) => (
          <span
            key={v}
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-primary-50 text-primary-700 text-xs font-medium"
          >
            {v}
            <button
              type="button"
              onClick={() => onRemoveValue(v)}
              className="text-primary-400 hover:text-primary-700"
              title="Remove value"
            >
              <X className="h-3 w-3" />
            </button>
          </span>
        ))}
        {attr.values.length === 0 && (
          <span className="text-xs text-slate-400 italic">No values yet — add one below</span>
        )}
      </div>

      <div className="flex gap-2">
        <Input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              if (draft.trim()) { onAddValue(draft); setDraft(''); }
            }
          }}
          placeholder={`Add ${attr.label.toLowerCase()} value`}
          className="max-w-xs h-8 text-sm"
        />
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => { if (draft.trim()) { onAddValue(draft); setDraft(''); } }}
          disabled={!draft.trim()}
        >
          Add
        </Button>
      </div>
    </div>
  );
}

// ─── Helper: convert VariantState → API payload ──────────────────────────────

export function variantsToPayload(state: VariantState): Array<{
  name: string;
  sku: string;
  options: Record<string, string>;
  price: number;
  stock: number;
  imageUrl?: string;
}> | undefined {
  if (state.variants.length === 0) return undefined;
  return state.variants.map((v) => ({
    name: v.name,
    // Generated SKUs include a random suffix because the DB constraint is
    // GLOBAL across all product_variants — two products sharing the same
    // option combo (e.g. "color:Red") would otherwise collide.
    sku:
      v.sku ||
      `VAR-${v.id.replace(/[^a-z0-9]+/gi, '').toUpperCase().slice(0, 12)}-${Math.random().toString(36).slice(2, 6).toUpperCase()}`,
    options: v.options,
    price: v.price,
    stock: v.stock,
    imageUrl: v.imageUrl || undefined,
  }));
}

export function emptyVariantState(): VariantState {
  return { attributes: [], variants: [] };
}

/**
 * Rebuild editor state from a product's existing variants so the edit flow is
 * fully seeded (attributes, values, and per-combo rows). Without this, opening
 * an existing product for edit would show an empty editor and a save would wipe
 * its variants. The attribute order/values are derived from the union of every
 * variant's `options` map.
 */
export function variantStateFromProduct(
  variants:
    | Array<{
        name?: string | null;
        sku?: string | null;
        options?: Record<string, string> | null;
        price?: number | string | null;
        stock?: number | null;
        imageUrl?: string | null;
      }>
    | null
    | undefined,
): VariantState {
  if (!variants || variants.length === 0) return emptyVariantState();

  const attrValues = new Map<string, Set<string>>();
  const order: string[] = [];
  for (const v of variants) {
    const opts = v.options && typeof v.options === 'object' ? v.options : {};
    for (const [k, val] of Object.entries(opts)) {
      if (!attrValues.has(k)) {
        attrValues.set(k, new Set());
        order.push(k);
      }
      attrValues.get(k)!.add(String(val));
    }
  }

  const attributes = order.map((k) => ({
    key: k,
    label: k.charAt(0).toUpperCase() + k.slice(1),
    values: Array.from(attrValues.get(k)!),
  }));

  const rows: VariantRow[] = variants.map((v, i) => {
    const opts = (v.options && typeof v.options === 'object' ? v.options : {}) as Record<string, string>;
    const comboId = order
      .filter((k) => opts[k] != null)
      .map((k) => `${k}:${opts[k]}`)
      .join('|');
    return {
      id: comboId || v.sku || `variant-${i}`,
      name: v.name || Object.values(opts).join(' / ') || `Variant ${i + 1}`,
      options: opts,
      sku: v.sku || '',
      price: Number(v.price) || 0,
      stock: Number(v.stock) || 0,
      imageUrl: v.imageUrl || undefined,
    };
  });

  return { attributes, variants: rows };
}

// ─── Internal: compact per-variant image uploader ────────────────────────────

interface VariantImageCellProps {
  value?: string;
  onChange: (url: string) => void;
}

function VariantImageCell({ value, onChange }: VariantImageCellProps) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);

  const handleFile = async (file: File) => {
    setUploading(true);
    try {
      const res = await adminApi.uploadImage(file);
      const url = res.data?.data?.url;
      if (!url) throw new Error('No URL in upload response');
      onChange(url);
    } catch (err) {
      const e = err as { response?: { data?: { message?: string } }; message?: string };
      toast.error('Upload failed', { description: e.response?.data?.message ?? e.message });
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  return (
    <div className="flex items-center gap-1.5">
      <button
        type="button"
        onClick={() => fileRef.current?.click()}
        disabled={uploading}
        title={value ? 'Replace image' : 'Upload image'}
        className="relative h-10 w-10 rounded-md border border-dashed border-slate-300 bg-slate-50 hover:border-primary-400 hover:bg-primary-50/40 flex items-center justify-center overflow-hidden flex-shrink-0 disabled:cursor-wait"
      >
        {value ? (
          <img src={value} alt="" className="h-full w-full object-cover" />
        ) : (
          <ImageIcon className="h-4 w-4 text-slate-400" />
        )}
        {uploading && (
          <span className="absolute inset-0 bg-white/80 flex items-center justify-center">
            <Loader2 className="h-4 w-4 text-primary-600 animate-spin" />
          </span>
        )}
      </button>

      {value ? (
        <button
          type="button"
          onClick={() => onChange('')}
          className="text-slate-400 hover:text-red-500"
          title="Remove image"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      ) : (
        <Upload className="h-3.5 w-3.5 text-slate-300" />
      )}

      <input
        ref={fileRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/gif"
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) void handleFile(f);
        }}
      />
    </div>
  );
}
